import { useMemo } from 'react'
import { KanbanBoard, Chip, type KanbanItem, type KanbanStage } from '@/components/ui'
import type { RegulatoryStage, RequestSummary, RequestType } from '../api/types'
import { accentForHex, CATEGORY_SHORT, PRIORITY_VARIANT } from '../utils/meta'

/**
 * Dynamic-stage Kanban for the regulatory board.
 *
 * Unlike sales-inbox (frozen stage enum + user-pref column order), the
 * columns here come straight from the server `RegulatoryStage[]` for the
 * active request_type. Column reorder persists server-side (stages are
 * org-shared config, not a per-user preference). Transitions are permissive
 * (kanban buckets) — the board only ever shows one request_type's stages at
 * a time, so cross-type drags are structurally impossible.
 */

interface CardItem extends KanbanItem {
  card: RequestSummary
}

export interface RegulatoryKanbanProps {
  requestType: RequestType
  stages: RegulatoryStage[]
  items: RequestSummary[]
  selectedId?: string
  onSelect: (card: RequestSummary) => void
  onMove: (card: RequestSummary, targetStageId: string) => void
  onReorder: (stageIds: string[]) => void
  /** View-only roles (sales): no dragging cards or columns. */
  readOnly?: boolean
}

export function RegulatoryKanban({
  requestType,
  stages,
  items,
  selectedId,
  onSelect,
  onMove,
  onReorder,
  readOnly = false,
}: RegulatoryKanbanProps) {
  const kanbanStages: KanbanStage[] = useMemo(
    () =>
      [...stages]
        .sort((a, b) => a.sequence - b.sequence)
        .map((s) => ({
          id: s.id,
          label: s.name,
          accent: accentForHex(s.color),
        })),
    [stages],
  )

  const kanbanItems: CardItem[] = useMemo(
    () =>
      items
        .filter((c) => c.stage_id != null)
        .map((c) => ({ id: c.id, stageId: c.stage_id as string, card: c })),
    [items],
  )

  const colorByStage = useMemo(() => {
    const m: Record<string, string | null> = {}
    for (const s of stages) m[s.id] = s.color
    return m
  }, [stages])

  return (
    <KanbanBoard<CardItem>
      stages={kanbanStages}
      items={kanbanItems}
      selectedId={selectedId}
      allowedTransitions={() => !readOnly}
      onAdvance={(item, _from, to) => onMove(item.card, to)}
      onSelect={(item) => onSelect(item.card)}
      onStageReorder={readOnly ? undefined : (next) => onReorder(next)}
      renderCard={(item) => (
        <RegulatoryCard
          card={item.card}
          stageColor={colorByStage[item.card.stage_id ?? '']}
        />
      )}
      scrollRestorationKey={`regulatory-${requestType}`}
    />
  )
}

function RegulatoryCard({
  card,
  stageColor,
}: {
  card: RequestSummary
  stageColor?: string | null
}) {
  // Server-chosen stage color is surfaced as an inline custom property —
  // exempt from the tokens-only rule because it's user data, not a
  // hardcoded design value.
  const style = stageColor
    ? ({ ['--stage-color' as string]: stageColor } as React.CSSProperties)
    : undefined
  return (
    <div
      style={style}
      className={`flex flex-col gap-1.5 ${
        card.is_overdue ? '-ml-3 border-l-2 border-l-danger pl-2.5' : ''
      }`}
      title={card.is_overdue ? 'Past due' : undefined}
    >
      <div className="flex items-center gap-2">
        {card.is_overdue ? (
          <span className="text-role-metadata font-medium text-danger">Overdue</span>
        ) : null}
        <Chip size="xs" variant="neutral">
          {CATEGORY_SHORT[card.category]}
        </Chip>
        <Chip size="xs" variant={PRIORITY_VARIANT[card.priority]}>
          {card.priority}
        </Chip>
      </div>
      <div
        className="text-role-data-primary truncate"
        title={card.title}
      >
        {card.title}
      </div>
      {card.product_names?.length ? (
        <div className="text-role-data-secondary truncate">
          {card.product_names.join(', ')}
          {card.lot_names?.length ? ` · Lot ${card.lot_names.join(', ')}` : ''}
        </div>
      ) : null}
      <div className="flex items-center gap-2 text-role-metadata">
        {card.due_date ? (
          <span className={card.is_overdue ? 'font-medium text-danger' : 'text-tertiary'}>
            Due {formatDate(card.due_date)}
          </span>
        ) : null}
        <span className="text-tertiary">
          {card.assigned_to_name ?? 'Unassigned'}
        </span>
        {card.source_message_index_id ? (
          <span className="text-tertiary" title="From email">📧</span>
        ) : null}
      </div>
      {card.doc_request ? (
        <div className="flex items-center gap-1.5 text-role-metadata">
          <Chip size="xs" variant={card.doc_request.status === 'vendor_submitted' ? 'warning'
            : card.doc_request.status === 'completed' ? 'success' : 'info'}>
            {card.doc_request.reference}
          </Chip>
          <span className="text-tertiary">
            {card.doc_request.status === 'open' ? 'Waiting for vendor'
              : card.doc_request.status === 'revoked' ? 'Cancelled'
                : `${card.doc_request.settled}/${card.doc_request.total} attached`}
          </span>
        </div>
      ) : null}
      {card.carla_summary ? (
        <div className="line-clamp-2 text-role-metadata italic" title={card.carla_summary}>
          {card.carla_summary}
        </div>
      ) : null}
    </div>
  )
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}
